import { Bell } from "lucide-react";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const priorityIcon: Record<string, string> = {
  urgent: "🔴",
  high: "🟡",
  normal: "🔵",
  low: "⚪",
};

const NotifItem = ({ n, onAction }: { n: Notification; onAction: (url: string) => void }) => (
  <div
    className={cn(
      "flex items-start gap-3 p-3 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-accent/50",
      n.status === "unread" && "bg-primary/5"
    )}
    onClick={() => n.action_url && onAction(n.action_url)}
  >
    <span className="text-sm mt-0.5">{priorityIcon[n.priority] ?? "🔵"}</span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate">{n.title}</p>
      {n.entity_label && (
        <p className="text-xs text-muted-foreground truncate">{n.entity_label}</p>
      )}
      <p className="text-xs text-muted-foreground mt-0.5">
        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
      </p>
    </div>
    {n.action_label && (
      <Badge variant="outline" className="shrink-0 text-[10px]">
        {n.action_label}
      </Badge>
    )}
  </div>
);

export const NotificationBell = () => {
  const { data: notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();

  const handleAction = (url: string, notifId: string) => {
    markRead.mutate(notifId);
    navigate(url);
  };

  const recent = notifications?.slice(0, 8) ?? [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => markAllRead.mutate()}>
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {recent.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              ✅ All caught up
            </div>
          ) : (
            recent.map((n) => (
              <NotifItem key={n.id} n={n} onAction={(url) => handleAction(url, n.id)} />
            ))
          )}
        </div>
        <div className="border-t px-3 py-2">
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => navigate("/controller/notifications")}>
            View All Notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
